import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

// models
import { MixpanelEvents } from '../../models/mixpanelEvents';

// services
import { HomeService } from '../../services/home.service';
import { MixpanelService } from '../../services/mixpanel.service';
import { ProductsService } from '../../services/products.service';
import { SessionService } from '../../services/session.service';

export enum CategoryValue {
	FOOD_AND_ALCOHOL = 'FOOD_AND_ALCOHOL',
	BABY_PRODUCTS = 'BABY_PRODUCTS',
	HEALTH_AND_BEAUTY = 'HEALTH_AND_BEAUTY',
	PET_STORES = 'PET_STORES',
	CLOTHING_AND_ACCESSORIES = 'CLOTHING_AND_ACCESSORIES',
	HOUSEHOLD_GOODS = 'HOUSEHOLD_GOODS',
	ELECTRONICS = 'ELECTRONICS',
}

export enum CategoryKey {
	FOOD_AND_ALCOHOL = 'DEMO.WELCOME_FORM.CATEGORY_PRODUCTS',
	BABY_PRODUCTS = 'DEMO.WELCOME_FORM.CATEGORY_CHILDREN',
	HEALTH_AND_BEAUTY = 'DEMO.WELCOME_FORM.CATEGORY_PHARMACY',
	PET_STORES = 'DEMO.WELCOME_FORM.CATEGORY_PETS',
	CLOTHING_AND_ACCESSORIES = 'DEMO.WELCOME_FORM.CATEGORY_APPAREL',
	HOUSEHOLD_GOODS = 'DEMO.WELCOME_FORM.CATEGORY_HOUSEHOLD',
	ELECTRONICS = 'DEMO.WELCOME_FORM.CATEGORY_ELECTRONICS',
}

export const categoriesMap: Record<CategoryKey, CategoryValue> = {
	[CategoryKey.FOOD_AND_ALCOHOL]: CategoryValue.FOOD_AND_ALCOHOL,
	[CategoryKey.BABY_PRODUCTS]: CategoryValue.BABY_PRODUCTS,
	[CategoryKey.HEALTH_AND_BEAUTY]: CategoryValue.HEALTH_AND_BEAUTY,
	[CategoryKey.PET_STORES]: CategoryValue.PET_STORES,
	[CategoryKey.CLOTHING_AND_ACCESSORIES]: CategoryValue.CLOTHING_AND_ACCESSORIES,
	[CategoryKey.HOUSEHOLD_GOODS]: CategoryValue.HOUSEHOLD_GOODS,
	[CategoryKey.ELECTRONICS]: CategoryValue.ELECTRONICS,
};

@Component({
    selector: 'app-welcome-demo-form-modal',
    templateUrl: './welcome-demo-form-modal.component.html',
    styleUrls: ['./welcome-demo-form-modal.component.scss'],
    standalone: false
})
export class WelcomeDemoFormModalComponent implements OnInit {

	welcomeForm: FormGroup;
	categories: Array<CategoryKey> = Object.keys(categoriesMap) as Array<CategoryKey>;
	sendingForm: boolean = false;
	allowedCategories: Array<string> = [];

	constructor(
		private fb: FormBuilder,
		private dialogRef: MatDialogRef<WelcomeDemoFormModalComponent>,
		private homeService: HomeService,
		private productsService: ProductsService,
		private mixPanelService: MixpanelService,
		private sessionService: SessionService
	) {}

	ngOnInit(): void {
		this.welcomeForm = this.fb.group({
			selectedCategories: this.fb.array([]) as FormArray<FormControl>
		});
		this.addCheckboxes();

		this.homeService.getDemoAssortmentCategories().subscribe((categories: string[]): void => {
			this.allowedCategories = categories;
		});
	}

	private addCheckboxes(): void {
		this.welcomeForm = this.fb.group({
			selectedCategories: this.fb.array(
				this.categories.map(() => new FormControl(true)),
				(formArray: FormArray) => formArray.controls.some(c => c.value) ? null : { required: true }
			)
		});

	}

	get selectedCategories(): FormArray<FormControl> {
		return this.welcomeForm.get('selectedCategories') as FormArray<FormControl>;
	}

	submit(): void {
		this.sendingForm = true;

		const selectedCategories: Array<CategoryKey> = this.selectedCategories.controls
			.map((control: FormControl, index: number): CategoryKey => control.value ? this.categories[index] : null)
			.filter((value: CategoryKey | null): boolean => value !== null);

		const payloadCategories: Array<CategoryValue> = selectedCategories.map((category: CategoryKey): CategoryValue => categoriesMap[category]);

		const allCategoriesValid: boolean = payloadCategories.every((category: string): boolean =>
			this.allowedCategories.includes(category)
		);

		if (!allCategoriesValid || payloadCategories.length === 0) {
			this.sendingForm = false;
			return;
		}

		this.sessionService.defaults.settings.welcomeDemoFormCompleted = true;
		this.sessionService.updateSettings$().subscribe();

		this.productsService.loadTestAssortment().subscribe({
			next: (): void => {
				this.homeService.setDemoAssortmentCategories(payloadCategories).subscribe({
					next: (): void => {
						this.mixPanelService.trackPage(
							MixpanelEvents.DemoCategoriesSelect,
							{ categories: payloadCategories.join(', ') }
						);
						this.dialogRef.close(true);
					},
					error: (): void => {
						this.sendingForm = false;
					}
				});
			},
			error: (): void => {
				this.sendingForm = false;
			}
		});
	}
}